import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as apiHelper from "../../Services/ApiHelper";

interface PatientData {
  id: number;
  aadhar_no: string | null;
  address: string;
  city: string;
  department_id: string;
  dob: string;
  file_no: string;
  gender: string;
  mno: string | null;
  alternate_mno: string | null;
  p_email: string | null;
  name: string;
  online_id: string | null;
  photo_url: string | null;
  notes: string;
  relation: string;
  visit_date: string;
  next_visit_date: string | null;
  status: string;
  counselling_remark_1: string;
  counselling_remark_2: string;
  spouse_name: string;
  fee: number;
  doctor_id: string;
  is_edit_allowed: boolean;
  edit_lock_reason?: string | null; // "SETTLED" | "CLOSED" from backend, null when editable
  uid: string;
  pndt: string | null;
  teleconsult: number;
  services_list: number[];
  fingerprint?: any[];
  alert_now?: boolean;
  alert_days?: string | number;
  alert_hrs?: string | number;
  alert_min?: string | number;
  visit_time?: string;
  hospital_unit_id?: string;
  visit_id?: number;
  weight?: string | number;
  height?: string | number;
  bp?: string;
  startCamera?: boolean;
  tpa_id?: number | null;
  [key: string]: any;
}

interface PatientVisitsData {
  data: Array<{
    id: number;
    type: string;
    patient_ipd_detail?: {
      id: number;
    };
    [key: string]: any;
  }>;
  [key: string]: any;
}

interface DamEntry {
  dam_id: number | string;
  current_use: string | null;
  ever_use: string | null;
  comment: string | null;
}

interface DamsState {
  patient_id: number | null;
  dams: DamEntry[];
  [key: string]: any;
}

interface DamsCommentState {
  damsComments: Record<number, string | null>;
  additionalComments: Record<string, string | null>;
}

interface InvestigationAdditionalData {
  ultrasound: string;
  ct_scan: string;
}

interface SoapData {
  subjective: string;
  objective: string;
  action: string;
  prognosis: string;
}

interface PhysicalExaminationData {
  motor: string;
  sensory: string;
  reflexes: string;
  tentative_diagnosis: string;
  additional_testing: string;
  c_spine_exam: string;
  lumbar_exam: string;
  shoulder_exam: string;
  knee_exam: string;
  elbow_exam: string;
  hand_wrist_exam: string;
  ankle_exam: string;
}

interface PatientDocumentState {
  folderId: number | null;
  folderName: string;
}

interface PatientVisitState {
  patientData: PatientData | null;
  patientVisits: PatientVisitsData | null;
  loading: boolean;
  error: string | null;
  visitId: string | null;
  // Alert settings
  alertDays: string;
  alertHours: string;
  alertMinutes: string;
  isAlertEnabled: boolean;
  // UI state
  buttonLoading: boolean;
  hospitalUnit: string;
  isDepartmentLocked: boolean;
  // Diagnosis state
  showDiagnosis: { show: boolean; data: any };
  diagnosisData: any[];
  diagnosisLoading: boolean;
  diagnosisSelected: Record<number, { label: any; value: any }>;
  allDiagnosis: any[];
  diagnosisButtonLoading: boolean;
  diagnosisPaginationData: any | null;
  // Treatment state
  showTreatment: { show: boolean; data: any };
  treatmentData: any[];
  treatmentLoading: boolean;
  treatmentButtonLoading: boolean;
  treatmentPaginationData: any | null;
  treatmentDates: { startDate: Date | null; endDate: Date | null };
  treatmentSearchQuery: string;
  // DAMS state
  damsData: DamsState | null;
  damsComments: DamsCommentState;
  damsButtonLoading: boolean;
  // Investigation state
  investigationData: any | null;
  investigationLoading: boolean;
  investigationButtonLoading: boolean;
  investigationAdditionalData: InvestigationAdditionalData;
  // SOAP state
  soapData: SoapData | null;
  soapLoading: boolean;
  soapButtonLoading: boolean;
  // Physical Examination state
  physicalExaminationData: PhysicalExaminationData | null;
  physicalExaminationLoading: boolean;
  physicalExaminationButtonLoading: boolean;
  // Docs tab state
  showPatientDocuments: PatientDocumentState;
  tpaOptions: any[];
}

const initialState: PatientVisitState = {
  patientData: null,
  patientVisits: null,
  loading: false,
  error: null,
  visitId: null,
  alertDays: "",
  alertHours: "",
  alertMinutes: "",
  isAlertEnabled: false,
  buttonLoading: false,
  hospitalUnit: "",
  isDepartmentLocked: false,
  showDiagnosis: { show: false, data: null },
  diagnosisData: [],
  diagnosisLoading: false,
  diagnosisSelected: {},
  allDiagnosis: [],
  diagnosisButtonLoading: false,
  diagnosisPaginationData: null,
  showTreatment: { show: false, data: null },
  treatmentData: [],
  treatmentLoading: false,
  treatmentButtonLoading: false,
  treatmentPaginationData: null,
  treatmentDates: { startDate: null, endDate: null },
  treatmentSearchQuery: "",
  damsData: null,
  damsComments: {
    damsComments: {},
    additionalComments: {},
  },
  damsButtonLoading: false,
  investigationData: null,
  investigationLoading: false,
  investigationButtonLoading: false,
  investigationAdditionalData: {
    ultrasound: "",
    ct_scan: "",
  },
  // SOAP state
  soapData: null,
  soapLoading: false,
  soapButtonLoading: false,
  // Physical Examination state
  physicalExaminationData: null,
  physicalExaminationLoading: false,
  physicalExaminationButtonLoading: false,
  // Docs tab state
  showPatientDocuments: {
    folderId: null,
    folderName: "",
  },
  tpaOptions: [],
};

// Async thunk to fetch patient details
// API endpoint: GET /api/patient/{patientId}?page=1
export const fetchPatientDetails = createAsyncThunk(
  "patientVisit/fetchPatientDetails",
  async (patientId: string, { rejectWithValue }) => {
    try {
      // Ensure query parameter ?page=1 is included
      // The API expects: patient/{patientId}?page=1
      const query = patientId.includes("?") ? patientId : `${patientId}?page=1`;
      const response = await apiHelper.getPatientDetailsWithVisits(query);

      if (response?.isSuccess && response?.code === 200) {
        return {
          patient: {
            ...response.data.patient,
            fingerprint: response.data.patient?.fingerprint || response.data.fingerprint || [],
            totals: response.data.patient_totals,
          },
          patientVisits: {
            ...response.data.patient_visits,
            patient_totals: response.data.patient_totals,
          },
        };
      } else {
        return rejectWithValue(response?.message || "Failed to fetch patient details");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred");
    }
  },
);

const patientVisitSlice = createSlice({
  name: "patientVisit",
  initialState,
  reducers: {
    setPatientData: (state, action: PayloadAction<any>) => {
      state.patientData = action.payload;
    },
    setVisitId: (state, action: PayloadAction<string>) => {
      state.visitId = action.payload;
    },
    setPatientVisits: (state, action: PayloadAction<any>) => {
      state.patientVisits = action.payload;
    },
    clearPatientData: (state) => {
      state.patientData = null;
      state.patientVisits = null;
      state.visitId = "";
      state.error = null;
      state.isDepartmentLocked = false;
    },
    updatePatientData: (state, action: PayloadAction<Partial<PatientData>>) => {
      if (state.patientData) {
        // Never apply `id` from partial updates: callers often spread visit/order API bodies where
        // top-level `id` is patient_visit id, which would corrupt patient (user) id. Canonical id
        // only comes from fetchPatientDetails.fulfilled.
        const { id: _ignored, ...patch } = action.payload as Partial<PatientData> & { id?: number };
        state.patientData = { ...state.patientData, ...patch };
      }
    },
    // Alert actions
    setAlertDays: (state, action: PayloadAction<string>) => {
      state.alertDays = action.payload;
    },
    setAlertHours: (state, action: PayloadAction<string>) => {
      state.alertHours = action.payload;
    },
    setAlertMinutes: (state, action: PayloadAction<string>) => {
      state.alertMinutes = action.payload;
    },
    setIsAlertEnabled: (state, action: PayloadAction<boolean>) => {
      state.isAlertEnabled = action.payload;
    },
    initializeAlertData: (state) => {
      if (state.patientData) {
        state.isAlertEnabled = state.patientData.alert_now || false;
        state.alertDays = (state.patientData.alert_days?.toString() || "").toString();
        state.alertHours = (state.patientData.alert_hrs?.toString() || "").toString();
        state.alertMinutes = (state.patientData.alert_min?.toString() || "").toString();
      }
    },
    // UI state actions
    setButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.buttonLoading = action.payload;
    },
    setHospitalUnit: (state, action: PayloadAction<string>) => {
      state.hospitalUnit = action.payload;
    },
    setDepartmentLocked: (state, action: PayloadAction<boolean>) => {
      state.isDepartmentLocked = action.payload;
    },
    // Diagnosis actions
    setShowDiagnosis: (state, action: PayloadAction<{ show: boolean; data: any }>) => {
      state.showDiagnosis = action.payload;
    },
    setDiagnosisData: (state, action: PayloadAction<any[]>) => {
      state.diagnosisData = action.payload;
    },
    setDiagnosisLoading: (state, action: PayloadAction<boolean>) => {
      state.diagnosisLoading = action.payload;
    },
    setDiagnosisSelected: (
      state,
      action: PayloadAction<Record<number, { label: any; value: any }>>,
    ) => {
      state.diagnosisSelected = action.payload;
    },
    updateDiagnosisSelected: (
      state,
      action: PayloadAction<{ visitId: number; value: { label: any; value: any } }>,
    ) => {
      state.diagnosisSelected[action.payload.visitId] = action.payload.value;
    },
    setAllDiagnosis: (state, action: PayloadAction<any[]>) => {
      state.allDiagnosis = action.payload;
    },
    setDiagnosisButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.diagnosisButtonLoading = action.payload;
    },
    setDiagnosisPaginationData: (state, action: PayloadAction<any | null>) => {
      state.diagnosisPaginationData = action.payload;
    },
    // Treatment actions
    setShowTreatment: (state, action: PayloadAction<{ show: boolean; data: any }>) => {
      state.showTreatment = action.payload;
    },
    setTreatmentData: (state, action: PayloadAction<any[]>) => {
      state.treatmentData = action.payload;
    },
    setTreatmentLoading: (state, action: PayloadAction<boolean>) => {
      state.treatmentLoading = action.payload;
    },
    setTreatmentButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.treatmentButtonLoading = action.payload;
    },
    setTreatmentPaginationData: (state, action: PayloadAction<any | null>) => {
      state.treatmentPaginationData = action.payload;
    },
    setTreatmentDates: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>,
    ) => {
      state.treatmentDates = action.payload;
    },
    setTreatmentSearchQuery: (state, action: PayloadAction<string>) => {
      state.treatmentSearchQuery = action.payload;
    },
    // DAMS actions
    setDamsData: (state, action: PayloadAction<DamsState | null>) => {
      state.damsData = action.payload;
    },
    updateDamsData: (state, action: PayloadAction<Partial<DamsState>>) => {
      if (state.damsData) {
        state.damsData = { ...state.damsData, ...action.payload };
      }
    },
    setDamsComments: (state, action: PayloadAction<DamsCommentState>) => {
      state.damsComments = action.payload;
    },
    updateDamsComment: (
      state,
      action: PayloadAction<{ id?: number; key?: string; value: string | null }>,
    ) => {
      if (action.payload.id !== undefined) {
        state.damsComments.damsComments[action.payload.id] = action.payload.value;
      } else if (action.payload.key) {
        state.damsComments.additionalComments[action.payload.key] = action.payload.value;
      }
    },
    setDamsButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.damsButtonLoading = action.payload;
    },
    resetDamsData: (state) => {
      state.damsData = null;
      state.damsComments = {
        damsComments: {},
        additionalComments: {},
      };
    },
    // Investigation actions
    setInvestigationData: (state, action: PayloadAction<any | null>) => {
      state.investigationData = action.payload;
    },
    setInvestigationLoading: (state, action: PayloadAction<boolean>) => {
      state.investigationLoading = action.payload;
    },
    setInvestigationButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.investigationButtonLoading = action.payload;
    },
    setInvestigationAdditionalData: (
      state,
      action: PayloadAction<Partial<InvestigationAdditionalData>>,
    ) => {
      state.investigationAdditionalData = {
        ...state.investigationAdditionalData,
        ...action.payload,
      };
    },
    resetInvestigationData: (state) => {
      state.investigationData = null;
      state.investigationAdditionalData = {
        ultrasound: "",
        ct_scan: "",
      };
    },
    // SOAP actions
    setSoapData: (state, action: PayloadAction<SoapData | null>) => {
      state.soapData = action.payload;
    },
    updateSoapData: (state, action: PayloadAction<Partial<SoapData>>) => {
      if (state.soapData) {
        state.soapData = { ...state.soapData, ...action.payload };
      } else {
        state.soapData = {
          subjective: "",
          objective: "",
          action: "",
          prognosis: "",
          ...action.payload,
        };
      }
    },
    setSoapLoading: (state, action: PayloadAction<boolean>) => {
      state.soapLoading = action.payload;
    },
    setSoapButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.soapButtonLoading = action.payload;
    },
    resetSoapData: (state) => {
      state.soapData = null;
    },
    // Physical Examination actions
    setPhysicalExaminationData: (state, action: PayloadAction<PhysicalExaminationData | null>) => {
      state.physicalExaminationData = action.payload;
    },
    updatePhysicalExaminationData: (
      state,
      action: PayloadAction<Partial<PhysicalExaminationData>>,
    ) => {
      if (state.physicalExaminationData) {
        state.physicalExaminationData = { ...state.physicalExaminationData, ...action.payload };
      } else {
        state.physicalExaminationData = {
          motor: "",
          sensory: "",
          reflexes: "",
          tentative_diagnosis: "",
          additional_testing: "",
          c_spine_exam: "",
          lumbar_exam: "",
          shoulder_exam: "",
          knee_exam: "",
          elbow_exam: "",
          hand_wrist_exam: "",
          ankle_exam: "",
          ...action.payload,
        };
      }
    },
    setPhysicalExaminationLoading: (state, action: PayloadAction<boolean>) => {
      state.physicalExaminationLoading = action.payload;
    },
    setPhysicalExaminationButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.physicalExaminationButtonLoading = action.payload;
    },
    resetPhysicalExaminationData: (state) => {
      state.physicalExaminationData = null;
    },
    // Docs tab actions
    setShowPatientDocuments: (state, action: PayloadAction<PatientDocumentState>) => {
      state.showPatientDocuments = action.payload;
    },
    resetShowPatientDocuments: (state) => {
      state.showPatientDocuments = {
        folderId: null,
        folderName: "",
      };
    },
    setTpaOptions: (state, action: PayloadAction<any[]>) => {
      state.tpaOptions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatientDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientDetails.fulfilled, (state, action) => {
        state.loading = false;
        // Preserve department_id if it was previously set and the API response doesn't include it
        const previousDepartmentId = state.patientData?.department_id;
        state.patientData = action.payload.patient;
        state.patientVisits = action.payload.patientVisits;
        // Initialize alert data when patient data is loaded
        if (action.payload.patient && state.patientData) {
          // Preserve department_id if it was previously set and is missing/empty in the response
          // Check if previousDepartmentId is a valid value (not null, undefined, empty string, or "0")
          const hasPreviousDepartmentId =
            previousDepartmentId !== null &&
            previousDepartmentId !== undefined &&
            previousDepartmentId !== "" &&
            String(previousDepartmentId) !== "0";

          // Check if the new department_id from API is missing or empty
          const newDepartmentId = state.patientData.department_id;
          const isNewDepartmentIdEmpty =
            !newDepartmentId ||
            newDepartmentId === "" ||
            newDepartmentId === null ||
            newDepartmentId === undefined ||
            String(newDepartmentId) === "0";

          // Preserve the previous department_id if it was set and the new one is empty
          if (hasPreviousDepartmentId && isNewDepartmentIdEmpty) {
            state.patientData.department_id = previousDepartmentId;
          }

          // Don't automatically lock department - allow changes based on permissions
          // Reset department lock state when fetching patient details
          // Department should only be disabled based on is_edit_allowed and patient status
          state.isDepartmentLocked = false;

          state.isAlertEnabled = action.payload.patient.alert_now || false;
          state.alertDays = (action.payload.patient.alert_days?.toString() || "").toString();
          state.alertHours = (action.payload.patient.alert_hrs?.toString() || "").toString();
          state.alertMinutes = (action.payload.patient.alert_min?.toString() || "").toString();
          state.hospitalUnit = action.payload.patient.hospital_unit_id || "";
        }
      })
      .addCase(fetchPatientDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPatientData,
  setVisitId,
  setPatientVisits,
  clearPatientData,
  updatePatientData,
  setAlertDays,
  setAlertHours,
  setAlertMinutes,
  setIsAlertEnabled,
  initializeAlertData,
  setButtonLoading,
  setHospitalUnit,
  setDepartmentLocked,
  setShowDiagnosis,
  setDiagnosisData,
  setDiagnosisLoading,
  setDiagnosisSelected,
  updateDiagnosisSelected,
  setAllDiagnosis,
  setDiagnosisButtonLoading,
  setDiagnosisPaginationData,
  setShowTreatment,
  setTreatmentData,
  setTreatmentLoading,
  setTreatmentButtonLoading,
  setTreatmentPaginationData,
  setTreatmentDates,
  setTreatmentSearchQuery,
  setDamsData,
  updateDamsData,
  setDamsComments,
  updateDamsComment,
  setDamsButtonLoading,
  resetDamsData,
  setInvestigationData,
  setInvestigationLoading,
  setInvestigationButtonLoading,
  setInvestigationAdditionalData,
  resetInvestigationData,
  setSoapData,
  updateSoapData,
  setSoapLoading,
  setSoapButtonLoading,
  resetSoapData,
  setPhysicalExaminationData,
  updatePhysicalExaminationData,
  setPhysicalExaminationLoading,
  setPhysicalExaminationButtonLoading,
  resetPhysicalExaminationData,
  setShowPatientDocuments,
  resetShowPatientDocuments,
  setTpaOptions,
} = patientVisitSlice.actions;
export default patientVisitSlice.reducer;
